import { BundleProps } from 'utils/types';
import { Box, Stack } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useEffect, useRef, useState } from 'react';
import {
  EBadgeType,
  EBundlesInternalViewModel,
  EProductPriority,
} from 'utils/enums';
import { StyledProductMadnessBundle } from './style';
import BundleSubProduct from './BundleProduct/sub';
import BundleMainProduct from './BundleProduct/main';
import { ProductMadnessBundlePrice } from './BundlePrice';
import { BadgeData, IEmblemBadge } from 'components/Badge/types';
import { generateSalePercentageBasedOnType, moveProductGrid } from 'utils';
import jackpotBadge from 'assets/Badges/jackpot-badge.png';
import bigFishBadge from 'assets/Badges/big-fish-casino-badge.png';
import { useGetBadge } from 'utils/useGetBadge';

const mapGameToBadgeProps: Partial<
  Record<EBundlesInternalViewModel, { img: any; textColor: string }>
> = {
  [EBundlesInternalViewModel.JACKPOT_SLOTS]: {
    img: jackpotBadge,
    textColor: '#481172',
  },
  [EBundlesInternalViewModel.BIG_FISH_CASINO]: {
    img: bigFishBadge,
    textColor: '#0E2C72',
  },
};

export const JackpotSlotsBundle: React.FC<BundleProps> = ({
  backgroundSrc,
  backgroundColor = { colorOne: '#000088' },
  prices,
  productList = [
    {
      textFontColorHex: '#ffabff',
      images: {
        product:
          'https://animal-kingdom-media.s3.amazonaws.com/asset-2.png?a=a',
      },
      quantity: 100,
      prefix: '',
      suffix: '',
      priority: EProductPriority.MAIN,
      quantityDisplay: '100',
      publisherProductId: '',
    },
  ],
  bundleButtonTextColor,
  badges = [],
  emblemBadges = [],
  salePercentage = 0,
  bundleRef,
  inMobileMock = false,
  loaded,
  salePercentageDisplayType,
  gameName,
  translations = { includes: 'Includes' },
}) => {
  const subItemsGrid = useRef<HTMLDivElement>(null);
  const [isDoneScrolling, setIsDoneScrolling] = useState(false);
  const [showScroller, setShowScroller] = useState(false);
  const [emblemBadgeOld, setEmblemBadgeOld] = useState<any>(null);
  const emblemBadgeNew = useGetBadge<IEmblemBadge>(emblemBadges);

  useEffect(() => {
    if (Array.isArray(badges) && badges.length > 0) {
      const foundEmblem = badges?.find((b: BadgeData) => {
        return (
          b.badge?.type === EBadgeType.EMBLEM || b.type === EBadgeType.EMBLEM
        );
      });
      setEmblemBadgeOld(foundEmblem?.badge?.emblem || foundEmblem?.emblem);
    } else {
      setEmblemBadgeOld(null);
    }
  }, [badges]);

  const emblemBadge = emblemBadgeNew ?? emblemBadgeOld;

  const moveGrid = (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e?.stopPropagation();
    const subItemsGridElement = subItemsGrid?.current;
    if (subItemsGridElement as HTMLDivElement) {
      moveProductGrid({
        subItemsGridElement: subItemsGridElement as HTMLDivElement,
        isDoneScrolling,
        setIsDoneScrolling,
      });
    }
  };

  // Trigger re-rendering when screen bundleSize changing
  useEffect(() => {
    let timeoutId: any;

    const shouldShowScrollbar = () => {
      setShowScroller(
        subItemsGrid?.current
          ? subItemsGrid.current.scrollWidth -
              subItemsGrid.current.clientWidth >
              15
          : false,
      );
    };

    shouldShowScrollbar();

    const handleResize = () => {
      clearTimeout(timeoutId);
      shouldShowScrollbar();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [subItemsGrid.current, loaded]);

  const saleBadgeTextColor =
    gameName &&
    mapGameToBadgeProps[gameName as EBundlesInternalViewModel]?.textColor;
  const saleImage =
    gameName && mapGameToBadgeProps[gameName as EBundlesInternalViewModel]?.img;

  return (
    <StyledProductMadnessBundle
      backgroundSrc={backgroundSrc}
      backgroundColor={backgroundColor}
      textColor={bundleButtonTextColor}
      className={
        'jackpot-slots-bundle' + (inMobileMock ? ' bundle--iphone-mock' : '')
      }
      ref={bundleRef}
      saleBadgeTextColor={saleBadgeTextColor || 'white'}
    >
      <Box className="jackpot-slots-bundle__container">
        <Box className="products">
          {salePercentage > 0 && (
            <div className="jackpot-slots-bundle__sale-badge">
              <img
                src={saleImage}
                alt={'bg sale image'}
                style={{ width: '120px' }}
              />
              <p className="jackpot-slots-bundle__sale-badge__text">
                {generateSalePercentageBasedOnType(
                  salePercentage,
                  salePercentageDisplayType,
                ) + ` MORE`}
              </p>
            </div>
          )}
          <Box className="main-product">
            {productList && productList.length > 0 ? (
              <BundleMainProduct
                quantityDisplay={productList[0].quantityDisplay}
                strokedSaleQuantity={productList[0].strokedSaleQuantity}
                publisherProductId={productList[0].publisherProductId}
                images={productList[0].images}
                quantity={productList[0].quantity}
                salePercentage={salePercentage}
                salePercentageDisplayType={salePercentageDisplayType}
              />
            ) : (
              <div className={'main-product-placeholder'}></div>
            )}
            <ProductMadnessBundlePrice prices={prices} />
          </Box>
          {productList.length > 1 && (
            <Box className={'sub-items'} display={'flex'} alignItems={'center'}>
              <span className={'sub-items__prefix-text'}>
                {translations.includes}
              </span>
              <Box className="sub-items-grid" ref={subItemsGrid}>
                {productList?.slice(1).map((product, i) => (
                  <Stack justifyContent="center" alignItems="center">
                    <BundleSubProduct
                      quantityDisplay={product.quantityDisplay}
                      productType={product.productType}
                      key={product.index}
                      textFontColorHex={product.textFontColorHex}
                      images={product.images}
                      quantity={product.quantity}
                      publisherProductId={product.publisherProductId}
                      rarityProductInfo={product.rarityProductInfo}
                      badgeText={product.badgeText}
                      name={product.name}
                      index={i}
                      itemRef={bundleRef}
                    />
                  </Stack>
                ))}
              </Box>
              {showScroller && (
                <Stack className="scroller">
                  <button onClick={(e) => moveGrid(e)}>
                    {isDoneScrolling ? (
                      <ArrowBackIosNewIcon
                        sx={{
                          color: 'white',
                          fontSize: 12,
                        }}
                      />
                    ) : (
                      <ArrowForwardIosIcon
                        sx={{
                          color: 'white',
                          fontSize: 12,
                        }}
                      />
                    )}
                  </button>
                </Stack>
              )}
            </Box>
          )}

          {emblemBadge && (
            <div className="jackpot-slots-bundle__emblem-badge">
              <img alt={'emblem-badge'} src={emblemBadge.imageUrl} />
            </div>
          )}
        </Box>
      </Box>
    </StyledProductMadnessBundle>
  );
};
